import { useCallback } from "react";
import type { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from "react-query";
import { useMutation, useQuery } from "react-query";

import { encodeJwt, getConfig } from "@jti/fe-common";
import type {
  DeviceResponse,
  IUser,
  MyDevicesResponse,
  RegisterDeviceError,
  RemoveDevicesResponse,
} from "@jti/rest-api-types";

import { MUTATION_KEYS, QUERY_KEYS } from "../../../constants/queryKeys";
import { DeviceApi } from "../../../services";
import { getUser } from "../../../utils";

const initDeviceApi = (user: IUser) => {
  const config = getConfig();
  const { first_name, last_name, magento_token, unique_id, uuid, email, ciam_token } = user;
  const token = encodeJwt({
    unique_id,
    first_name,
    last_name,
    email,
    ciam_token,
    magento_token,
    uuid,
    tenant_code: config.CAS_X_DES_AK_TENANT_TOKEN,
    ciam_tenant_token: config.CAS_X_DES_AK_TENANT_TOKEN,
  });

  const deviceAPI = DeviceApi.init(token);
  return deviceAPI;
};
export const DEVICE_REGISTERED_CODE = 202;
export const DEVICE_INVALID_REGISTER_CODE = 406;
export const DEVICE_ALREADY_REGISTERED_ANOTHER_ACCOUNT_CODE = 409;

export type DeviceRegisterStatusErrorType = {
  statusCode: typeof DEVICE_INVALID_REGISTER_CODE | typeof DEVICE_ALREADY_REGISTERED_ANOTHER_ACCOUNT_CODE;
  message?: string;
};

export type DeviceRegistrationStatusType = {
  isLoading: boolean;
  isError: boolean;
  isIdle: boolean;
  isFetching: boolean;
  error: DeviceRegisterStatusErrorType | null;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<undefined, DeviceRegisterStatusErrorType>>;
};

export const deviceErrorCodes: {
  [key: string]: string;
} = {
  400: "profiles.devices.notification.bad_request_title",
  401: "profiles.devices.notification.unauthorized_user_title",
  406: "profiles.devices.notification.invalid_registration_code_title",
  409: "profiles.devices.notification.registered_already_another_user_title",
  500: "profiles.devices.notification.processing_error_title",
};

export const deviceErrorActions: {
  [key: string]: string;
} = {
  400: "profiles.devices.notification.action.try_later",
  401: "profiles.devices.notification.action.try_later",
  500: "profiles.devices.notification.action.try_later",
};

export type DevicesType = {
  data: MyDevicesResponse | undefined;
  isLoadingData: boolean;
  isErrorData: boolean;
  isLoadingRegisterDevice: boolean;
  isRegisterDeviceError: boolean;
  deleteDevice: (id: string) => Promise<MyDevicesResponse>;
  registerDeviceError: RegisterDeviceError | null;
  registerDevice: (id: string, token?: string, ciamToken?: string) => Promise<DeviceResponse>;
  assignDeviceToUser: (deviceId: string, ciamToken?: string) => Promise<DeviceResponse>;
  resetRegisterDevice: () => void;
  refetch: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<MyDevicesResponse, unknown>>;
};

type UseDevicesOptions = {
  enableDevicesQuery?: boolean;
};

export const useDevices = (options?: UseDevicesOptions): DevicesType => {
  const user = getUser();
  const deviceAPI = initDeviceApi(user);
  const { uuid, ciam_token } = user;
  const { data, isLoading, isError, refetch } = useQuery(QUERY_KEYS.devices, () => deviceAPI.myDevices(uuid), {
    enabled: options?.enableDevicesQuery ?? Boolean(uuid),
  });

  const deleteMutation = useMutation<MyDevicesResponse, RemoveDevicesResponse, { id: string; consumerId: string }>(
    MUTATION_KEYS.deleteDevice,
    ({ id, consumerId }) => deviceAPI.deleteDevice(id, consumerId)
  );

  const registerMutation = useMutation<
    DeviceResponse,
    RegisterDeviceError,
    { registrationCode: string; token?: string; ciamToken?: string }
  >(MUTATION_KEYS.registerDevice, ({ registrationCode, token, ciamToken: ciamTokenOverride }) =>
    deviceAPI.registerDevice(registrationCode, uuid, token, ciamTokenOverride ?? ciam_token)
  );

  const registerDevice = useCallback(
    async (registrationCode: string, token?: string, ciamTokenOverride?: string) =>
      await registerMutation.mutateAsync({ registrationCode, token, ciamToken: ciamTokenOverride }),
    [registerMutation]
  );

  const assignDeviceToUserMutation = useMutation<
    DeviceResponse,
    RegisterDeviceError,
    { deviceId: string; userId?: string; ciamToken?: string }
  >(MUTATION_KEYS.assignDeviceToUser, ({ deviceId, userId, ciamToken: ciamTokenOverride }) =>
    deviceAPI.assignDeviceToUser(deviceId, userId, ciamTokenOverride)
  );

  const assignDeviceToUser = useCallback(
    async (deviceId: string, ciamTokenOverride?: string) =>
      await assignDeviceToUserMutation.mutateAsync({ deviceId, userId: uuid, ciamToken: ciamTokenOverride }),
    [assignDeviceToUserMutation, uuid]
  );

  const deleteDevice = useCallback(
    async (id: string) => {
      if (uuid === undefined) {
        return Promise.reject("Consumer ID is not defined");
      }
      return await deleteMutation.mutateAsync({ id, consumerId: uuid });
    },
    [deleteMutation, uuid]
  );

  return {
    data,
    isLoadingData: isLoading,
    isErrorData: isError,
    deleteDevice,
    registerDevice,
    assignDeviceToUser,
    resetRegisterDevice: registerMutation.reset,
    isRegisterDeviceError: registerMutation.isError,
    isLoadingRegisterDevice: registerMutation.isLoading,
    registerDeviceError: registerMutation.error,
    refetch,
  };
};

export const useDeviceRegistrationStatus = (id: string): DeviceRegistrationStatusType => {
  const user = getUser();
  const deviceAPI = initDeviceApi(user);

  const { isLoading, isFetching, isError, error, isIdle, refetch } = useQuery<undefined, DeviceRegisterStatusErrorType>(
    QUERY_KEYS.deviceRegistrationStatus,
    () => deviceAPI.checkDeviceIdForRegistration(id),
    {
      retry: false,
      enabled: false,
      refetchOnWindowFocus: false,
    }
  );

  return { isLoading, isError, isFetching, isIdle, error, refetch };
};
